import { createContext, ReactNode, useContext, useState } from "react";
import { LuCheck, LuTriangleAlert, LuX } from "react-icons/lu";

export type SnackbarType = "success" | "error";

export interface SnackbarMessage {
  id: string;
  message: string;
  type: SnackbarType;
  duration?: number | null;
  isVisible?: boolean;
  onClick?: () => void;
}

interface SnackbarProps {
  type?: SnackbarType;
  duration?: number | null;
  onClick?: () => void;
}

interface SnackbarContextType {
  showSnackbar: (message: string, params?: SnackbarProps) => void;
  messages: SnackbarMessage[];
  hideSnackbar: (id: string) => void;
  hideAllSnackbars: () => void;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(
  undefined
);

export function useSnackbar() {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error("useSnackbar must be used within a SnackbarProvider");
  }
  return context;
}

interface SnackbarProviderProps {
  children: ReactNode;
}

export function SnackbarProvider({ children }: SnackbarProviderProps) {
  const [messages, setMessages] = useState<SnackbarMessage[]>([]);

  const showSnackbar = (message: string, options?: SnackbarProps) => {
    const { type = "success", duration = 5000, onClick } = options || {};
    const id = Date.now().toString();
    const newMessage: SnackbarMessage = {
      id,
      message,
      type,
      duration,
      isVisible: false,
      onClick,
    };

    setMessages((prev) => [...prev, newMessage]);

    // Trigger fade in animation
    setTimeout(() => {
      setMessages((prev) =>
        prev.map((msg) => (msg.id === id ? { ...msg, isVisible: true } : msg))
      );
    }, 10);

    // Only set up auto-hide if duration is not null
    if (duration !== null) {
      // Start fade out animation before auto-hide
      setTimeout(() => {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === id ? { ...msg, isVisible: false } : msg
          )
        );
      }, duration - 300); // Start fade out 300ms before removal

      // Remove from DOM after fade out
      setTimeout(() => {
        hideSnackbar(id);
      }, duration);
    }
  };

  const hideSnackbar = (id: string) => {
    // Start fade out animation
    setMessages((prev) =>
      prev.map((msg) => (msg.id === id ? { ...msg, isVisible: false } : msg))
    );

    // Remove from DOM after fade out
    setTimeout(() => {
      setMessages((prev) => prev.filter((msg) => msg.id !== id));
    }, 300);
  };

  const hideAllSnackbars = () => {
    setMessages([]);
  };

  const getSnackbarStyles = () => {
    // Uniform dark surface for all notification types
    return "bg-dark-surface-secondary text-dark-text";
  };

  const getSnackbarIcon = (type: SnackbarType) => {
    switch (type) {
      case "success":
        return <LuCheck size={18} className="text-[#4ADE80]" />;
      case "error":
        return <LuTriangleAlert size={18} className="text-primary-pink" />;
      default:
        return null;
    }
  };

  return (
    <SnackbarContext.Provider
      value={{ showSnackbar, messages, hideSnackbar, hideAllSnackbars }}
    >
      {children}

      {/* Snackbar Container - z-[10001] to be above all modals */}
      <div
        className="fixed top-4
      left-0 right-0
      flex flex-col items-center z-[10001] gap-2 pointer-events-none"
      >
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`${getSnackbarStyles()}
              px-4 py-3 rounded-lg shadow-lg
              flex items-center justify-center gap-2 w-fit mx-4
              transition-all duration-300 pointer-events-auto
              ${msg.isVisible ? "opacity-100" : "opacity-0"}
              ${msg.onClick ? "cursor-pointer" : ""}`}
            onClick={
              msg.onClick
                ? () => {
                    msg.onClick!();
                    hideSnackbar(msg.id);
                  }
                : undefined
            }
          >
            {getSnackbarIcon(msg.type)}
            <span className="text-sm">{msg.message}</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                hideSnackbar(msg.id);
              }}
              className="ml-2 hover:bg-white/10 rounded-full p-1 transition-all duration-300"
              aria-label="Close snackbar"
            >
              <LuX size={16} />
            </button>
          </div>
        ))}
      </div>
    </SnackbarContext.Provider>
  );
}
